import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Mountain, MapPin, Star, ArrowRight, Users, Eye, TrendingUp, Shield, Phone, ChevronRight, Trees, Waves, Home, Heart, Compass, Building2, Bike, Car, Camera, Coffee, Sunrise } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import TAGLINE_MAP from '../data/taglineData';

const API = process.env.REACT_APP_BACKEND_URL;

// Map LLM-returned icon names to lucide-react components
const ICON_MAP = {
  Mountain, Trees, Waves, Home, Star, MapPin, Users, Shield,
  Heart, Compass, Building2, Bike, Car, Camera, Coffee, Sunrise,
};

// Hardcoded showpiece data for valleys with hand-curated extras (Tirthan).
// All other valleys render dynamically from /api/valleys/:slug.
const VALLEY_DATA = {
  tirthan: {
    name: 'Tirthan Valley',
    state: 'Himachal Pradesh',
    tagline: 'Where the Great Himalayan National Park begins',
    elevation: '1,600m',
    heroImage: '/tirthan_hero.jpg',
    description: [
      'The Tirthan Valley is one of the last unspoiled valleys in the Western Himalayas. Named after the Tirthan River — a crystalline tributary of the Beas — it runs along the edge of the Great Himalayan National Park, a UNESCO World Heritage Site.',
      'There are no malls here. No traffic jams. No neon signs. Just deodar forests, trout streams, stone-and-wood villages, and the kind of silence that makes you hear your own heartbeat.',
      'GoHimalayan has been quietly curating the best stays in this valley — from riverside cottages run by third-generation families to boutique lodges hidden in the forest canopy. Every property on this page has been personally visited and vetted.'
    ],
    valleyPartners: [
      {
        slug: 'rajucottage',
        name: "Raju Bharti's Cottage",
        role: 'Valley Partner',
        image: 'https://dynamic-media-cdn.tripadvisor.com/media/photo-o/04/79/e4/87/raju-bharti-s-guest-house.jpg?w=1200&h=900&s=1',
        quote: 'Raju Bharti has been welcoming travellers to Gushaini for over 20 years. His cottage is the beating heart of the valley — a place where trout is caught in the morning and served at dinner, where the ropeway across the Tirthan is still the only way in.',
        stats: { years: '20+', guests: '10,000+', rating: '4.7' }
      }
    ],
    collectivePartners: [
      {
        slug: 'bluehouse',
        name: 'Bluehouse by the River',
        image: 'https://customer-assets.emergentagent.com/job_bluehouse-site/artifacts/j1m8q6xp_IMG_2872.JPG',
        badge: 'Collective 100'
      },
      {
        slug: 'eaglenest',
        name: "Eagle's Nest",
        image: 'https://tirthan-stay-eagle-nest.com/wp-content/uploads/2022/06/tirthan-valley-stay-eagle-nest-107.jpg',
        badge: 'Collective 100'
      }
    ],
    valleyStats: {
      stays: 7,
      valleys: 'Tirthan & Kullu',
      partners: 4,
      avgRating: '4.5'
    }
  }
};

export default function ValleyPage() {
  const { slug = 'tirthan' } = useParams();
  const isShowpiece = !!VALLEY_DATA[slug];

  // ─── Showpiece path (Tirthan) — preserves exact existing behaviour ───────
  if (isShowpiece) {
    return <ShowpieceValleyPage slug={slug} />;
  }
  // ─── Dynamic path — every other valley reads from /api/valleys/:slug ─────
  return <DynamicValleyPage slug={slug} />;
}


function ShowpieceValleyPage({ slug }) {
  const [valleyStays, setValleyStays] = useState([]);
  const [loading, setLoading] = useState(true);
  const valley = VALLEY_DATA[slug];

  useEffect(() => {
    fetch(`${API}/api/curated/stays`)
      .then(r => r.json())
      .then(data => {
        const stays = Array.isArray(data) ? data : data.stays || [];
        const filtered = stays.filter(s => {
          const v = (s.valley || s.region || s.location || '').toLowerCase();
          return v.includes('tirthan') || v.includes('kullu');
        });
        const getBadgeCategory = (s) => {
          const n = (s.display_name || '').toLowerCase();
          if (s.partnership_status !== 'partner') return 'discovered';
          if (n.includes('raju')) return 'valley_partner';
          if (n.includes('brown dipper') || n.includes('browndipper')) return 'featured';
          if (n.includes('villa aavaas') || n.includes('villaaavaas')) return 'featured';
          return 'collective';
        };
        const buckets = { collective: [], featured: [], valley_partner: [], discovered: [] };
        filtered.forEach(s => buckets[getBadgeCategory(s)].push(s));
        const order = ['collective', 'discovered', 'valley_partner', 'featured'];
        const mixed = [];
        let added = true;
        while (added) {
          added = false;
          for (const cat of order) {
            if (buckets[cat].length > 0) {
              mixed.push(buckets[cat].shift());
              added = true;
            }
          }
        }
        setValleyStays(mixed);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Banner stats — count THIS valley only (Tirthan ≠ Kullu) so the headline
  // number reflects this slug, even though the listing below also surfaces
  // neighbouring Kullu stays for discoverability.
  const tirthanOnlyCount = valleyStays.filter(s => {
    const v = (s.valley || s.region || '').toLowerCase();
    return v.includes(slug);
  }).length;
  const partnerCountInValley = valleyStays.filter(s => {
    const v = (s.valley || s.region || '').toLowerCase();
    return v.includes(slug) && (s.partnership_status || '').toLowerCase() === 'partner';
  }).length;

  return (
    <div className="min-h-screen bg-white" data-testid="valley-page">
      <SEOHead
        title={`${valley.name} — Curated Stays`}
        description={valley.description[0]}
        image={valley.heroImage}
      />

      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5">
              <Mountain className="h-7 w-7 text-[#2C5F8D]" strokeWidth={2.5} />
              <span className="text-lg font-bold tracking-tight text-gray-900" style={{fontFamily: 'Playfair Display, serif'}}>GoHimalayan</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/discovered" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">All Stays</Link>
              <Link to="/partner" className="text-sm bg-gray-900 text-white px-4 py-2 rounded-full hover:bg-gray-800 transition-colors">List Your Stay</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative h-[85vh] overflow-hidden" data-testid="valley-hero">
        <img src={valley.heroImage} alt={valley.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-white/80 text-xs tracking-widest uppercase">GoHimalayan Valley</span>
              <span className="px-3 py-1 bg-[#2C5F8D]/80 backdrop-blur-sm rounded-full text-white text-xs tracking-widest uppercase">{tirthanOnlyCount || valley.valleyStats.stays} Curated Stays</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-3" style={{fontFamily: 'Playfair Display, serif'}}>{valley.name}</h1>
            <p className="text-xl text-white/70 max-w-xl" style={{fontFamily: 'Playfair Display, serif', fontStyle: 'italic'}}>{valley.tagline}</p>
            <div className="flex items-center gap-8 mt-8">
              <div className="text-white/60 text-sm"><span className="text-white font-semibold text-lg">{valley.elevation}</span><br/>elevation</div>
              <div className="w-px h-10 bg-white/20" />
              <div className="text-white/60 text-sm"><span className="text-white font-semibold text-lg">{partnerCountInValley || valley.valleyStats.partners}</span><br/>partner stays</div>
              <div className="w-px h-10 bg-white/20" />
              <div className="text-white/60 text-sm"><span className="text-white font-semibold text-lg">{valley.state}</span><br/>region</div>
            </div>
          </div>
        </div>
      </section>

      {/* Valley Story */}
      <section className="py-24 px-6 lg:px-8" data-testid="valley-story">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs tracking-[0.3em] text-[#2C5F8D] uppercase mb-8">The Valley</p>
          {valley.description.map((para, i) => (
            <p key={i} className="text-lg text-gray-600 leading-relaxed mb-6" style={{fontFamily: 'Playfair Display, serif', fontStyle: i === 0 ? 'italic' : 'normal'}}>{para}</p>
          ))}
        </div>
      </section>

      {/* Valley Partners */}
      <section className="bg-[#f8f6f3] py-24 px-6 lg:px-8" data-testid="valley-partner">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-8 h-px bg-[#2C5F8D]" />
            <p className="text-xs tracking-[0.3em] text-[#2C5F8D] uppercase">Valley Partners</p>
          </div>
          {valley.valleyPartners.map((partner, idx) => (
            <div key={partner.slug} className={`grid lg:grid-cols-2 gap-12 items-center ${idx > 0 ? 'mt-20 pt-16 border-t border-stone-200' : ''}`}>
              <div className={`relative ${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                <Link to={`/collective/${partner.slug}`}>
                  <img src={partner.image} alt={partner.name} className="w-full aspect-[4/3] object-cover rounded-2xl hover:scale-[1.02] transition-transform duration-500" />
                </Link>
                <div className="absolute top-4 left-4 pointer-events-none" style={{opacity: 0.9}}>
                  <div className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-2.5 py-1.5 rounded-md shadow-sm border border-stone-100">
                    <svg className="w-3.5 h-3.5 text-[#3a5a40] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                    </svg>
                    <span className="text-[9px] font-semibold text-stone-500 tracking-[0.12em] uppercase" style={{fontFamily: 'Georgia, serif'}}>Valley Partner</span>
                  </div>
                </div>
              </div>
              <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Playfair Display, serif'}}>{partner.name}</h2>
                <p className="text-[#2C5F8D] text-sm tracking-widest uppercase mb-8">{partner.role} — {valley.name}</p>
                <p className="text-gray-600 leading-relaxed text-lg mb-8" style={{fontFamily: 'Playfair Display, serif', fontStyle: 'italic'}}>{partner.quote}</p>
                <div className="flex items-center gap-8 mb-8">
                  <div><p className="text-2xl font-bold text-gray-900">{partner.stats.years}</p><p className="text-xs text-gray-500 uppercase tracking-wider">Years hosting</p></div>
                  <div><p className="text-2xl font-bold text-gray-900">{partner.stats.guests}</p><p className="text-xs text-gray-500 uppercase tracking-wider">Guests welcomed</p></div>
                  <div><p className="text-2xl font-bold text-gray-900">{partner.stats.rating}</p><p className="text-xs text-gray-500 uppercase tracking-wider">Rating</p></div>
                </div>
                <Link to={`/collective/${partner.slug}`} className="inline-flex items-center gap-2 text-[#2C5F8D] font-medium hover:gap-3 transition-all">
                  Read the full story <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Collective 100 Partners */}
      <section className="py-24 px-6 lg:px-8" data-testid="collective-partners">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#2C5F8D]" />
            <p className="text-xs tracking-[0.3em] text-[#2C5F8D] uppercase">Collective 100</p>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{fontFamily: 'Playfair Display, serif'}}>Partner Stays in {valley.name}</h2>
          <p className="text-gray-500 mb-12 max-w-xl">Exclusive properties hand-selected for their character, hospitality, and connection to the valley.</p>
          <div className="grid md:grid-cols-2 gap-8">
            {valley.collectivePartners.map((partner) => (
              <Link key={partner.slug} to={`/collective/${partner.slug}`} className="group relative rounded-2xl overflow-hidden aspect-[16/10]" data-testid={`partner-card-${partner.slug}`}>
                <img src={partner.image} alt={partner.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-1.5 pointer-events-none" style={{opacity: 0.85}}>
                  <div className="relative flex items-center bg-[#3a5a40]/80 backdrop-blur-sm px-3 py-1.5 rounded-md">
                    <span className="absolute right-2 font-bold text-white/15 leading-none select-none" style={{fontSize: '36px', letterSpacing: '-2px'}}>100</span>
                    <svg className="w-4 h-4 text-white/90 mr-1.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                    </svg>
                    <div className="relative z-10 flex flex-col leading-tight">
                      <span className="text-[10px] font-semibold text-white tracking-wide" style={{fontFamily: 'Georgia, serif'}}>GoHimalayan</span>
                      <span className="text-[7px] font-medium text-white/70 tracking-[0.2em] uppercase">Collective</span>
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl font-bold text-white mb-1" style={{fontFamily: 'Playfair Display, serif'}}>{partner.name}</h3>
                  <p className="text-white/70 text-sm italic" style={{fontFamily: 'Playfair Display, serif'}}>{TAGLINE_MAP[partner.slug] || ''}</p>
                  <div className="flex items-center gap-2 mt-3 text-white/60 text-sm group-hover:text-white/90 transition-colors">Read story <ArrowRight className="h-3.5 w-3.5" /></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* All Valley Stays */}
      <section className="bg-gray-50 py-24 px-6 lg:px-8" data-testid="all-valley-stays">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-gray-400" />
            <p className="text-xs tracking-[0.3em] text-gray-500 uppercase">All Stays</p>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{fontFamily: 'Playfair Display, serif'}}>Every stay in the valley</h2>
          <p className="text-gray-500 mb-12 max-w-xl">From riverside cottages to forest lodges — {valleyStays.length || valley.valleyStats.stays} curated stays across {valley.name} and the surrounding hills.</p>
          {loading ? (
            <p className="text-gray-400 text-center py-12">Loading stays...</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {valleyStays.map((stay, i) => {
                const name = stay.display_name || stay.name || '';
                const img = (stay.images || [])[0];
                let imgUrl = typeof img === 'object' ? img.url || img.src : img;
                // Strip absolute preview-domain prefixes so images resolve same-origin
                if (typeof imgUrl === 'string') {
                  imgUrl = imgUrl
                    .replace('https://alpine-rentals-7.emergent.host', '')
                    .replace('https://homestead-partner.preview.emergentagent.com', '');
                }
                // Tier-driven routing — uses booking_source (canonical slug) so
                // featured/collective tiers always link to the right page without
                // a hardcoded name lookup.
                const tier = (stay.partnership_status || '').toLowerCase();
                const isFeatured = tier === 'featured';
                const isCollective = tier === 'partner';
                const isValleyPartner = tier === 'valley_partner';
                const bs = (stay.booking_source || '').trim();
                const partnerSlug = (isCollective || isValleyPartner) && bs ? bs : null;
                const featuredSlug = isFeatured && bs
                  ? bs
                  : name.split(',')[0].trim().toLowerCase().replace(/['']/g,'').replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');
                const taglineSlug = partnerSlug || featuredSlug;
                const customTagline = TAGLINE_MAP[taglineSlug] || stay.tagline || '';
                const linkTo = isCollective || isValleyPartner
                  ? `/collective/${partnerSlug || bs}`
                  : `/stay/${featuredSlug}`;
                return (
                  <Link key={i} to={linkTo} className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all group" data-testid={`valley-stay-${i}`}>
                    <div className="relative aspect-[4/3] overflow-hidden">
                      {imgUrl && <img src={imgUrl} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}

                      {/* Tier badge — single source of truth: partnership_status */}
                      {isValleyPartner ? (
                        <div className="absolute top-3 left-3 pointer-events-none" data-testid="valley-partner-badge" style={{opacity: 0.9}}>
                          <div className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-2.5 py-1.5 rounded-md shadow-sm border border-stone-100">
                            <svg className="w-3.5 h-3.5 text-[#3a5a40] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                            </svg>
                            <span className="text-[9px] font-semibold text-stone-500 tracking-[0.12em] uppercase" style={{fontFamily: 'Georgia, serif'}}>Valley Partner</span>
                          </div>
                        </div>
                      ) : isFeatured ? (
                        <div className="absolute top-3 left-3 pointer-events-none" data-testid="featured-badge">
                          <div className="flex items-center gap-1.5 bg-white/80 backdrop-blur-sm px-2.5 py-1 rounded-md" style={{boxShadow: '0 1px 4px rgba(0,0,0,0.08)'}}>
                            <svg className="w-3 h-3 text-[#2C5F8D] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                            </svg>
                            <span className="text-[9px] font-medium text-stone-500 tracking-[0.12em] uppercase" style={{fontFamily: 'Georgia, serif'}}>Featured</span>
                          </div>
                        </div>
                      ) : isCollective ? (
                        <div className="absolute top-3 left-3 flex items-center gap-1.5 pointer-events-none" data-testid="collective-badge" style={{opacity: 0.85}}>
                          <div className="relative flex items-center bg-[#3a5a40]/80 backdrop-blur-sm px-3 py-1.5 rounded-md">
                            <span className="absolute right-2 font-bold text-white/15 leading-none select-none" style={{fontSize: '36px', letterSpacing: '-2px'}}>100</span>
                            <svg className="w-4 h-4 text-white/90 mr-1.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                            </svg>
                            <div className="relative z-10 flex flex-col leading-tight">
                              <span className="text-[10px] font-semibold text-white tracking-wide" style={{fontFamily: 'Georgia, serif'}}>GoHimalayan</span>
                              <span className="text-[7px] font-medium text-white/70 tracking-[0.2em] uppercase">Collective</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="absolute top-3 left-3 pointer-events-none" data-testid="discovered-badge">
                          <div className="flex items-center gap-1.5 bg-white/80 backdrop-blur-sm px-2.5 py-1 rounded-md" style={{boxShadow: '0 1px 4px rgba(0,0,0,0.08)'}}>
                            <svg className="w-3 h-3 text-[#2C5F8D] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
                            </svg>
                            <span className="text-[9px] font-medium text-stone-500 tracking-[0.12em] uppercase" style={{fontFamily: 'Georgia, serif'}}>Discovered</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-base font-semibold text-gray-900 mb-1" style={{fontFamily: 'Playfair Display, serif'}}>{name}</h3>
                      {name.toLowerCase().includes('brown dipper') && (
                        <p className="text-[10px] text-stone-400 italic -mt-0.5 mb-1" style={{fontFamily: 'Playfair Display, serif'}}>by Humming Woods</p>
                      )}
                      <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {stay.valley || stay.region || stay.location}</p>
                      {customTagline && <p className="text-xs text-gray-400 mt-2 italic line-clamp-2" style={{fontFamily: 'Playfair Display, serif'}}>"{customTagline}"</p>}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Valley Highlights — The Tirthan Experience */}
      <section className="bg-[#f8f6f3] py-24 px-6 lg:px-8" data-testid="valley-highlights">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs tracking-[0.3em] text-[#2C5F8D] uppercase mb-4">What Awaits You</p>
          <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>The Tirthan Experience</h2>
          <p className="text-gray-500 mb-12 max-w-2xl" style={{fontFamily: 'Playfair Display, serif', fontStyle: 'italic'}}>
            A valley where every trail tells a story, every river bend hides a secret, and every village feels like stepping back a century.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Trees, title: 'Great Himalayan National Park', desc: 'A UNESCO World Heritage Site at your doorstep. Trek to Tirath glacier, spot western tragopans and snow leopards, or take a guided birding walk through ancient forests. Multi-day expeditions with camping available.' },
              { icon: Mountain, title: 'Jalori Pass & Serolsar Lake', desc: 'Drive up to Jalori Pass at 3,120m for panoramic Himalayan views. From there, a 5km forest trail leads to the sacred Serolsar Lake — an alpine mirror framed by silver firs. The trek to Raghupur Fort offers 360-degree mountain vistas.' },
              { icon: Waves, title: 'Choi Waterfall', desc: 'A sacred forest waterfall hidden near Gushaini. A 2km trail through pine and oak forest leads to a tall cascade tumbling into a natural pool — cold enough to take your breath away. The Chhoie Mata shrine sits silently beside it.' },
              { icon: Home, title: 'Sarchi Village & Lambri Top', desc: 'A high-altitude hamlet at 2,200m with centuries-old wooden Himachali houses and the Gara Durga temple. From here, a steep climb to Lambri Top rewards you with 360-degree views of the Tirthan and Seraj valleys, wildflower meadows, and absolute silence.' },
              { icon: MapPin, title: 'Jibhi & Chehni Kothi', desc: 'Just a valley away — Jibhi offers riverside cafes, wooden bridges, and forest walks. Don\'t miss Chehni Kothi, a 1,500-year-old free-standing tower, the tallest in the Western Himalayas. A half-day trip from any Tirthan stay.' },
              { icon: Waves, title: 'Trout Fishing on the Tirthan', desc: 'One of the last rivers in India where brown and rainbow trout swim wild. Get a permit, borrow a rod from your homestay host, and spend a morning casting into crystal-clear pools. Catch-and-cook is a Tirthan tradition.' },
              { icon: Star, title: 'Stargazing & Bonfires', desc: 'Zero light pollution means the Milky Way is visible to the naked eye. Most stays offer bonfire evenings under the stars — bring a blanket, a cup of rhododendron juice, and look up. Full moon nights turn the valley silver.' },
              { icon: Users, title: 'Barthad & Bashleo Pass', desc: 'For the serious trekker — a strenuous 20km trail connecting Tirthan Valley to Kullu Sarahan via the ancient Bashleo Pass. Overnight camping at high ridges with views of snow-covered peaks in every direction.' },
              { icon: Shield, title: 'Village Walks & Local Life', desc: 'Stone-and-slate Himachali villages where apricots dry on rooftops, looms click on verandas, and the chai is always ready. Walk through apple and cherry orchards, visit ancient temples, and eat home-cooked rajma-chawal that no restaurant can replicate.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-100">
                <item.icon className="h-6 w-6 text-[#2C5F8D] mb-4" />
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-8 text-center" data-testid="valley-cta">
        <div className="max-w-2xl mx-auto">
          <Mountain className="h-10 w-10 text-[#2C5F8D] mx-auto mb-6" strokeWidth={2} />
          <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>Own a property in {valley.name}?</h2>
          <p className="text-gray-500 mb-8">Join the GoHimalayan valley network. Whether you're a boutique lodge or a family-run homestay, we'll help travellers find you.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/partner" className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-full hover:bg-gray-800 transition-colors text-sm font-medium" data-testid="become-partner-btn">
              Become a Partner <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="https://wa.me/919871155963?text=Hi!%20I%20own%20a%20property%20in%20Tirthan%20Valley%20and%20want%20to%20list%20on%20GoHimalayan." target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 px-8 py-3 rounded-full hover:border-gray-400 transition-colors text-sm" data-testid="whatsapp-inquiry-btn">
              <Phone className="h-4 w-4" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mountain className="h-4 w-4 text-[#2C5F8D]" />
            <span className="text-sm text-gray-500">GoHimalayan</span>
          </div>
          <span className="text-xs text-gray-400">&copy; 2026 GoHimalayan. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// DynamicValleyPage — for the 74 valleys without hardcoded showpiece data.
// Reads master valley + linked stays from /api/valleys/:slug, renders the
// same visual blueprint as the Tirthan page (Hero → Story → Stays → CTA).
// ════════════════════════════════════════════════════════════════════════
function DynamicValleyPage({ slug }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/valleys/${slug}`)
      .then(r => {
        if (!r.ok) {
          if (r.status === 404) setNotFound(true);
          throw new Error('not found');
        }
        return r.json();
      })
      .then(d => {
        setData(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="valley-loading">
        <p className="text-gray-400 text-sm">Loading valley…</p>
      </div>
    );
  }
  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6" data-testid="valley-not-found">
        <Mountain className="h-10 w-10 text-stone-300 mb-4" />
        <h1 className="text-2xl text-stone-900 mb-2" style={{fontFamily: 'Playfair Display, serif'}}>Valley not found</h1>
        <p className="text-sm text-gray-500 mb-6">We don't have a page for "{slug}" yet.</p>
        <Link to="/discovered" className="text-sm text-[#2C5F8D] hover:underline">Browse all stays →</Link>
      </div>
    );
  }

  const { valley, state, stays = [] } = data;
  const stayCount = stays.length;
  const altitudeMid = Math.round((valley.altitude_range_m[0] + valley.altitude_range_m[1]) / 2);
  const introParagraphs = (valley.editorial_intro || '').split(/\n\s*\n/).filter(Boolean);
  const partnerStays = stays.filter(s => (s.partnership_status || '').toLowerCase() === 'partner' || (s.partnership_status || '').toLowerCase() === 'valley_partner');
  const showPartnerSpotlight = partnerStays.length > 0;
  const heroImage = valley.hero_image || (stays[0] && (stays[0].images || [])[0] && (stays[0].images[0].url || stays[0].images[0]));

  return (
    <div className="min-h-screen bg-white" data-testid="valley-page-dynamic">
      <SEOHead
        title={`${valley.name} — Curated Stays`}
        description={(introParagraphs[0] || '').slice(0, 200)}
        image={heroImage}
      />

      {/* Navigation — matches showpiece */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5">
              <Mountain className="h-7 w-7 text-[#2C5F8D]" strokeWidth={2.5} />
              <span className="text-lg font-bold tracking-tight text-gray-900" style={{fontFamily: 'Playfair Display, serif'}}>GoHimalayan</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/discovered" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">All Stays</Link>
              <Link to="/partner" className="text-sm bg-gray-900 text-white px-4 py-2 rounded-full hover:bg-gray-800 transition-colors">List Your Stay</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative h-[85vh] overflow-hidden" data-testid="valley-hero">
        {heroImage ? (
          <img src={heroImage} alt={valley.name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-stone-700 to-stone-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-white/80 text-xs tracking-widest uppercase">GoHimalayan Valley</span>
              {stayCount > 0 && (
                <span className="px-3 py-1 bg-[#2C5F8D]/80 backdrop-blur-sm rounded-full text-white text-xs tracking-widest uppercase">
                  {stayCount} Curated {stayCount === 1 ? 'Stay' : 'Stays'}
                </span>
              )}
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-3" style={{fontFamily: 'Playfair Display, serif'}}>{valley.name}</h1>
            {state && (
              <p className="text-xl text-white/70 max-w-xl" style={{fontFamily: 'Playfair Display, serif', fontStyle: 'italic'}}>
                {state.name}
              </p>
            )}
            <div className="flex items-center gap-8 mt-8 flex-wrap">
              <div className="text-white/60 text-sm">
                <span className="text-white font-semibold text-lg">~{altitudeMid}m</span><br/>elevation
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div className="text-white/60 text-sm">
                <span className="text-white font-semibold text-lg">~{valley.drive_from_nearest_airport_hours}h</span><br/>nearest airport
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div className="text-white/60 text-sm">
                <span className="text-white font-semibold text-lg">{stayCount}</span><br/>curated stays
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Valley Story */}
      {introParagraphs.length > 0 && (
        <section className="py-24 px-6 lg:px-8" data-testid="valley-story">
          <div className="max-w-3xl mx-auto">
            <p className="text-xs tracking-[0.3em] text-[#2C5F8D] uppercase mb-8">The Valley</p>
            {introParagraphs.map((para, i) => (
              <p key={i} className="text-lg text-gray-600 leading-relaxed mb-6" style={{fontFamily: 'Playfair Display, serif', fontStyle: i === 0 ? 'italic' : 'normal'}}>
                {para}
              </p>
            ))}
            {valley.anchor_villages && valley.anchor_villages.length > 0 && (
              <div className="mt-8 pt-8 border-t border-stone-100 text-sm text-gray-500">
                <span className="font-medium text-gray-700">Anchor villages: </span>
                {valley.anchor_villages.join(' · ')}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Partner Spotlight (auto-shown only when partner stays exist) */}
      {showPartnerSpotlight && (
        <section className="bg-[#f8f6f3] py-24 px-6 lg:px-8" data-testid="valley-partner-spotlight">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-12">
              <div className="w-8 h-px bg-[#2C5F8D]" />
              <p className="text-xs tracking-[0.3em] text-[#2C5F8D] uppercase">
                {partnerStays.length === 1 ? 'Partner Spotlight' : 'Partner Spotlights'}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {partnerStays.slice(0, 4).map((p) => {
                const img = (p.images || [])[0];
                const imgUrl = typeof img === 'object' ? (img.url || img.src) : img;
                const partnerSlug = (p.display_name || '').toLowerCase().includes('raju') ? 'rajucottage'
                  : (p.display_name || '').toLowerCase().includes('bluehouse') ? 'bluehouse'
                  : (p.display_name || '').toLowerCase().includes('eagle') ? 'eaglenest'
                  : (p.display_name || '').toLowerCase().includes('tra ') ? 'tra'
                  : null;
                const featuredSlug = (p.display_name || '').split(',')[0].trim().toLowerCase().replace(/['']/g,'').replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');
                const linkTo = partnerSlug ? `/collective/${partnerSlug}` : `/stay/${featuredSlug}`;
                return (
                  <Link key={p.display_name} to={linkTo} className="group relative rounded-2xl overflow-hidden aspect-[16/10]">
                    {imgUrl && <img src={imgUrl} alt={p.display_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="inline-block px-2 py-0.5 bg-white/15 backdrop-blur-sm rounded text-white/80 text-[10px] tracking-widest uppercase mb-2">Collective</span>
                      <h3 className="text-2xl text-white" style={{fontFamily: 'Playfair Display, serif'}}>{p.display_name}</h3>
                      {p.tagline && <p className="text-white/70 text-sm italic mt-1" style={{fontFamily: 'Playfair Display, serif'}}>"{p.tagline}"</p>}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* All Valley Stays */}
      <section className="bg-gray-50 py-24 px-6 lg:px-8" data-testid="all-valley-stays">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-gray-400" />
            <p className="text-xs tracking-[0.3em] text-gray-500 uppercase">All Stays</p>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{fontFamily: 'Playfair Display, serif'}}>Every stay in the valley</h2>
          <p className="text-gray-500 mb-12 max-w-xl">
            {stayCount === 0
              ? `${valley.name} is on our editorial radar — properties coming soon. Know one we should feature?`
              : `${stayCount} curated ${stayCount === 1 ? 'stay' : 'stays'} across ${valley.name}${valley.anchor_villages?.length ? ' and the surrounding hills' : ''}.`}
          </p>

          {stayCount === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center" data-testid="empty-stays">
              <Mountain className="h-8 w-8 text-stone-300 mx-auto mb-4" />
              <p className="text-sm text-gray-500 mb-6">No stays curated for this valley yet.</p>
              <Link to="/partner" className="inline-flex items-center gap-2 text-sm text-[#2C5F8D] hover:underline" data-testid="suggest-property-link">
                Suggest a property <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {stays.map((stay, i) => {
                const name = stay.display_name || stay.name || '';
                const img = (stay.images || [])[0];
                let imgUrl = typeof img === 'object' ? img.url || img.src : img;
                if (typeof imgUrl === 'string') {
                  imgUrl = imgUrl
                    .replace('https://alpine-rentals-7.emergent.host', '')
                    .replace('https://homestead-partner.preview.emergentagent.com', '');
                }
                const isPartner = stay.partnership_status === 'partner';
                const partnerSlug = name.toLowerCase().includes('raju') ? 'rajucottage'
                  : name.toLowerCase().includes('bluehouse') ? 'bluehouse'
                  : name.toLowerCase().includes('eagle') ? 'eaglenest'
                  : name.toLowerCase().includes('tra ') ? 'tra'
                  : name.toLowerCase().includes('brown dipper') ? 'browndipper'
                  : name.toLowerCase().includes('villa aavaas') ? 'villaaavaas' : null;
                const featuredSlug = name.split(',')[0].trim().toLowerCase().replace(/['']/g,'').replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');
                const taglineSlug = partnerSlug || featuredSlug;
                const customTagline = TAGLINE_MAP[taglineSlug] || stay.tagline || '';
                const linkTo = partnerSlug ? `/collective/${partnerSlug}` : `/stay/${featuredSlug}`;
                return (
                  <Link key={i} to={linkTo} className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all group" data-testid={`valley-stay-${i}`}>
                    <div className="relative aspect-[4/3] overflow-hidden">
                      {imgUrl && <img src={imgUrl} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                      <div className="absolute top-3 left-3 pointer-events-none">
                        <div className="flex items-center gap-1.5 bg-white/85 backdrop-blur-sm px-2.5 py-1 rounded-md" style={{boxShadow: '0 1px 4px rgba(0,0,0,0.08)'}}>
                          <svg className="w-3 h-3 text-[#2C5F8D] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            {isPartner ? <path d="m8 3 4 8 5-5 5 15H2L8 3z"/> : (<><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></>)}
                          </svg>
                          <span className="text-[9px] font-medium text-stone-500 tracking-[0.12em] uppercase" style={{fontFamily: 'Georgia, serif'}}>
                            {isPartner ? 'Collective' : 'Discovered'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-base font-semibold text-gray-900 mb-1" style={{fontFamily: 'Playfair Display, serif'}}>{name}</h3>
                      <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {stay.valley || stay.region || stay.location}</p>
                      {customTagline && <p className="text-xs text-gray-400 mt-2 italic line-clamp-2" style={{fontFamily: 'Playfair Display, serif'}}>"{customTagline}"</p>}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Experience Cards (AI-generated, mirrors Tirthan's "What Awaits You") */}
      {Array.isArray(valley.experience_cards) && valley.experience_cards.length > 0 && (
        <section className="bg-[#f8f6f3] py-24 px-6 lg:px-8" data-testid="valley-experience-cards">
          <div className="max-w-6xl mx-auto">
            <p className="text-xs tracking-[0.3em] text-[#2C5F8D] uppercase mb-4">What Awaits You</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>The {valley.name.split(' Valley')[0].split(' &')[0]} Experience</h2>
            <p className="text-gray-500 mb-12 max-w-2xl" style={{fontFamily: 'Playfair Display, serif', fontStyle: 'italic'}}>
              {introParagraphs[0] ? introParagraphs[0].split('. ').slice(0, 1).join('. ') + '.' : `Real places, real walks, real meals — six things to do in ${valley.name}.`}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {valley.experience_cards.map((card, i) => {
                const Icon = ICON_MAP[card.icon] || Mountain;
                return (
                  <div key={i} className="bg-white rounded-xl p-6 border border-gray-100" data-testid={`exp-card-${i}`}>
                    <Icon className="h-6 w-6 text-[#2C5F8D] mb-4" />
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">{card.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{card.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 px-6 lg:px-8 text-center" data-testid="valley-cta">
        <div className="max-w-2xl mx-auto">
          <Mountain className="h-10 w-10 text-[#2C5F8D] mx-auto mb-6" strokeWidth={2} />
          <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>Own a property in {valley.name}?</h2>
          <p className="text-gray-500 mb-8">Join the GoHimalayan valley network. Whether you're a boutique lodge or a family-run homestay, we'll help travellers find you.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/partner" className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-full hover:bg-gray-800 transition-colors text-sm font-medium" data-testid="become-partner-btn">
              Become a Partner <ArrowRight className="h-4 w-4" />
            </Link>
            <a href={`https://wa.me/919871155963?text=Hi!%20I%20own%20a%20property%20in%20${encodeURIComponent(valley.name)}%20and%20want%20to%20list%20on%20GoHimalayan.`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 px-8 py-3 rounded-full hover:border-gray-400 transition-colors text-sm" data-testid="whatsapp-inquiry-btn">
              <Phone className="h-4 w-4" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mountain className="h-4 w-4 text-[#2C5F8D]" />
            <span className="text-sm text-gray-500">GoHimalayan</span>
          </div>
          <span className="text-xs text-gray-400">&copy; 2026 GoHimalayan. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}

